import React, { useEffect, useState } from "react";
import { Modal } from "@picklebet/ui";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { useWalletPermission } from "@/features/wallet/hooks/useWalletPermission";
import { CheckPermissions } from "@/features/wallet/components/CheckPermissions";
import type { DepositMethodId } from "../../types";
import {
  DepositWizard,
  DepositWizardSkeleton,
} from "../deposit-wizard/DepositWizard";
import { DepositUnavailable } from "./DepositUnavailable";

import * as styles from "./DepositModal.module.scss";

type DepositModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Prefills the deposit amount input */
  initialAmountCents?: number;
  /**
   * Whether or not to warn about losing betslip state when depositing. Namely
   * used for Interac deposits where the user is redirected externally to
   * complete their deposit */
  warnBetslipLoss?: boolean;
  /**
   * Closes the modal on a completed deposit instead of showing the success
   * screen. Used when quick depositing
   */
  closeOnSuccess?: boolean;
  /**
   * Methods to withhold on top of the account's own availability. Pass a stable
   * reference — a fresh array each render re-derives the method list */
  excludedMethods?: readonly DepositMethodId[];
};

const DepositModal = ({
  isOpen,
  onOpenChange,
  initialAmountCents,
  warnBetslipLoss,
  closeOnSuccess,
  excludedMethods,
}: DepositModalProps) => {
  const { permission: depositPermission, isLoading: isPermissionLoading } =
    useWalletPermission("deposit");
  const canViewDepositModal = depositPermission !== "LOGIN_REQUIRED";

  // held above Modal.Root because an in-flight payment has to seal the modal's
  // own dismiss routes, not just the wizard's inputs
  const [submitting, setSubmitting] = useState(false);

  const close = () => onOpenChange(false);

  const isSupportOpen = useHelpCenterStore(
    (state) => state.visible || state.chatOpen,
  );

  useEffect(() => {
    if (isSupportOpen && isOpen) {
      close();
    }
  }, [isSupportOpen]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      return;
    }

    close();
  };

  if (!canViewDepositModal) {
    return null;
  }

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={!submitting}
    >
      <Modal.Body className={styles.modalBody}>
        <Modal.Title visuallyHidden>Deposit funds</Modal.Title>
        <Modal.Subtitle visuallyHidden>
          Enter an amount and select a payment method
        </Modal.Subtitle>

        {/* permission almost always resolves to the wizard, so stand in at its
        height. A spinner is ~70px shorter and jumps the modal on arrival. */}
        {isPermissionLoading ? (
          <DepositWizardSkeleton />
        ) : depositPermission === "GRANTED" ? (
          <DepositWizard
            initialAmountCents={initialAmountCents}
            warnBetslipLoss={warnBetslipLoss}
            closeOnSuccess={closeOnSuccess}
            excludedMethods={excludedMethods}
            close={close}
            submitting={submitting}
            setSubmitting={setSubmitting}
          />
        ) : (
          <>
            <Modal.Header>
              <Modal.Heading />
            </Modal.Header>

            <CheckPermissions type="deposit">
              <DepositUnavailable />
            </CheckPermissions>
          </>
        )}
      </Modal.Body>
    </Modal.Root>
  );
};

export { DepositModal, type DepositModalProps };
